import { Head } from "@inertiajs/react"
import { BadgeCheck, Building2, ShieldCheck, UserSearch, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import AdminLayout from "@/layouts/admin-layout"
import { typedPatch } from "@/lib/typed-router"
import type { AdminUpdateVerification } from "@/types/router-mutations"
import type { AdminVerificationsIndex } from "@/types/serializers"

type Props = AdminVerificationsIndex

function handleVerify(id: number, type: string) {
  typedPatch<AdminUpdateVerification>(`/admin/verifications/${id}`, {
    verification: { type, action: "verify" },
  })
}

function handleReject(id: number, type: string) {
  typedPatch<AdminUpdateVerification>(`/admin/verifications/${id}`, {
    verification: { type, action: "reject" },
  })
}

export default function VerificationsIndex({
  unverified_scouts,
  unverified_clubs,
}: Props) {
  const hasNone =
    unverified_scouts.length === 0 && unverified_clubs.length === 0

  return (
    <AdminLayout>
      <Head title="Verificaciones | Admin | KrownFT" />

      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <ShieldCheck className="text-krown-300 h-6 w-6" />
          <h1 className="text-2xl font-bold">Verificaciones</h1>
        </div>

        {hasNone && (
          <Card>
            <CardContent className="py-12 text-center">
              <BadgeCheck className="text-muted-foreground/50 mx-auto mb-4 h-12 w-12" />
              <p className="text-muted-foreground text-lg">
                No hay verificaciones pendientes
              </p>
            </CardContent>
          </Card>
        )}

        {unverified_scouts.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <UserSearch className="text-krown-300 h-5 w-5" />
                Scouts Pendientes
                <span className="ml-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-500/20 px-1.5 text-xs font-bold text-orange-400">
                  {unverified_scouts.length}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {unverified_scouts.map((scout) => (
                <div
                  key={scout.id}
                  className="border-border flex items-center gap-4 rounded-lg border p-4"
                >
                  <div className="bg-krown-600 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white">
                    {scout.user.first_name[0]}
                    {scout.user.last_name[0]}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{scout.user.full_name}</p>
                    <p className="text-muted-foreground text-sm">
                      {scout.user.email}
                    </p>
                    {scout.organization_name && (
                      <p className="text-muted-foreground text-sm">
                        {scout.organization_name}
                        {scout.role_title && ` — ${scout.role_title}`}
                      </p>
                    )}
                    {scout.city && scout.country && (
                      <p className="text-muted-foreground text-xs">
                        {scout.city}, {scout.country}
                      </p>
                    )}
                  </div>

                  <div className="flex shrink-0 gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleVerify(scout.id, "ScoutProfile")}
                    >
                      <BadgeCheck className="mr-1 h-3.5 w-3.5" />
                      Verificar
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleReject(scout.id, "ScoutProfile")}
                    >
                      <X className="mr-1 h-3.5 w-3.5" />
                      Rechazar
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {unverified_clubs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="text-krown-300 h-5 w-5" />
                Clubes Pendientes
                <span className="ml-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-500/20 px-1.5 text-xs font-bold text-orange-400">
                  {unverified_clubs.length}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {unverified_clubs.map((club) => (
                <div
                  key={club.id}
                  className="border-border flex items-center gap-4 rounded-lg border p-4"
                >
                  <div className="bg-krown-600 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white">
                    {club.name[0]}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{club.name}</p>
                    <p className="text-muted-foreground text-sm">
                      {club.user.email}
                    </p>
                    {club.division && (
                      <p className="text-muted-foreground text-sm">
                        {club.division}
                      </p>
                    )}
                    {club.city && club.country && (
                      <p className="text-muted-foreground text-xs">
                        {club.city}, {club.country}
                      </p>
                    )}
                  </div>

                  <div className="flex shrink-0 gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleVerify(club.id, "ClubProfile")}
                    >
                      <BadgeCheck className="mr-1 h-3.5 w-3.5" />
                      Verificar
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleReject(club.id, "ClubProfile")}
                    >
                      <X className="mr-1 h-3.5 w-3.5" />
                      Rechazar
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  )
}
